import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: React.ReactNode;
  name?: string;
  showReload?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[ERROR-BOUNDARY] ${this.props.name || 'unknown'} crashed:`, {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDataFormatError =
        this.state.error?.message?.includes('toFixed') ||
        this.state.error?.message?.includes('charAt') ||
        this.state.error?.message?.includes('Objects are not valid') ||
        this.state.error?.message?.includes('undefined');

      return (
        <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <div>
            <p className="text-sm font-medium text-destructive">
              {isDataFormatError
                ? 'Results data format error — the data structure from the server does not match what the UI expects.'
                : `Something went wrong in "${this.props.name || 'unknown'}"`}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {isDataFormatError
                ? 'This is usually caused by a mismatch between the Go backend output and the TypeScript frontend types. Check the schema validator logs for details.'
                : this.state.error?.message}
            </p>
          </div>
          {this.props.showReload && (
            <Button variant="outline" size="sm" onClick={this.handleReload}>
              Reload page
            </Button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
